import { Body, Heading2 } from '@components/utils/text';
import { SEARCH } from '@constants';
import styled from '@emotion/styled';
import { TrackEvent } from '@enums';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { BasicDoc, StateResultsProvided } from 'react-instantsearch-core';
import { connectStateResults } from 'react-instantsearch-dom';
import { useDebounce } from 'react-use';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.spacing.sm};
    flex: 1;
    align-items: center;
    text-align: center;
    padding: ${(props) => props.theme.spacing.sm};
    margin-top: ${(props) => props.theme.spacing.md};
`;

const MessageTitle = styled(Heading2)`
    word-break: break-word;
`;

const MessageBody = styled(Body)``;

const StateResults = ({
    searchState,
    allSearchResults,
}: StateResultsProvided<BasicDoc>) => {
    const { t } = useTranslation(SEARCH);
    const router = useRouter();
    const { track } = useAnalytics();
    const reducer = (accumulator: number, currentValue: number): number =>
        accumulator + currentValue;

    const sendSearchAnalytics = () => {
        if (searchState.query) {
            const searchResults = Object.entries(allSearchResults).map(
                ([index, results]) => {
                    return {
                        index_name: index,
                        index_type: index.split('_')[1],
                        hits_count: results.nbHits,
                        hits: results.hits.map((hit) => {
                            return {
                                objectID: hit.objectID,
                                UID: hit.UID,
                            };
                        }),
                    };
                }
            );
            const query = searchState.query;
            const searchAnalytics = {
                query,
                company_slug: router.query.company as string,
                product_slug: router.query.product as string,
                locale: router.locale as string,
                searchResults,
                total_hits_count: searchResults
                    .map((indexResult) => indexResult.hits_count)
                    .reduce(reducer),
            };
            track(TrackEvent.Search, searchAnalytics);
        }
    };

    // Debounce the analytics call to avoid sending too many requests.
    // Unused vars does not mean this is not used.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isReady, cancel] = useDebounce(sendSearchAnalytics, 2000, [
        searchState.query,
        allSearchResults,
    ]);

    if (
        !(allSearchResults && searchState.query) ||
        Object.values(allSearchResults)
            .map((index) => index.nbHits)
            .reduce(reducer) !== 0
    ) {
        return null;
    }

    return (
        <Container>
            <MessageTitle>
                {t('no_result_title')}
                <br />
                {`"${searchState.query}"`}
            </MessageTitle>
            <MessageBody>{t('no_result_body')}</MessageBody>
        </Container>
    );
};

const CustomStateResults = connectStateResults(StateResults);
export default CustomStateResults;
